import { ConnectionInfo, SpeedTestResult, TestState } from '../types';
import { Server } from 'shared-types';

const BASE_URL = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '');

const resolveServerUrl = (url: string): string => {
  if (url.startsWith('http')) {
    return url;
  }
  return `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

export const fetchConnectionInfo = async (): Promise<ConnectionInfo> => {
  try {
    const response = await fetch('https://ipinfo.io/json');
    if (!response.ok) throw new Error('Failed to fetch IP info');
    const data = await response.json();
    let latitude: number | undefined, longitude: number | undefined;
    if (data.loc) [latitude, longitude] = data.loc.split(',').map(parseFloat);
    let isp = data.org || 'Your ISP', asNumber: string | undefined;
    const orgMatch = data.org?.match(/^(AS\d+)\s(.+)/);
    if (orgMatch) {
      asNumber = orgMatch[1];
      isp = orgMatch[2].replace(/^autonomous system number for /i, '');
    }
    return { ip: data.ip || '127.0.0.1', isp, as: asNumber, serverLocation: (data.city && data.country) ? `${data.city}, ${data.country}` : 'Unknown Location', latitude, longitude };
  } catch (error) {
    console.error("Error fetching connection info:", error);
    return { ip: 'Unavailable', isp: 'Could not fetch ISP information', serverLocation: 'Location not found', error: true };
  }
};

interface SpeedTestCallbacks {
  onStateChange: (state: Extract<TestState, 'testing-ping' | 'testing-download' | 'testing-upload'>) => void;
  onPingUpdate: (ping: number, jitter: number) => void;
  onDownloadUpdate: (speed: number) => void;
  onUploadUpdate: (speed: number) => void;
  onComplete: (result: SpeedTestResult) => void;
  onError: (message: string) => void;
  onProgress: (progress: number) => void;
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
const appendCacheBuster = (url: string) => url + (url.includes('?') ? '&' : '?') + `t=${Date.now()}&r=${Math.random()}`;
const createSpeedSmoother = (windowSize = 5) => {
  const readings: number[] = [];
  return (newSpeed: number): number => {
    readings.push(newSpeed);
    if (readings.length > windowSize) readings.shift();
    if (readings.length === 0) return 0;
    return readings.reduce((a, b) => a + b, 0) / readings.length;
  };
};

export const runSpeedTest = async (callbacks: SpeedTestCallbacks, serverUrl: string, connectionMode: 'single' | 'multi', durationSeconds: number): Promise<void> => {
  try {
    const resolvedServerUrl = resolveServerUrl(serverUrl);
    const result: SpeedTestResult = { ping: 0, jitter: 0, download: 0, upload: 0 };
    const WARMUP_MS = 3000;
    const TEST_DURATION_MS = durationSeconds * 1000;

    // 1. Ping Test
    callbacks.onStateChange('testing-ping');
    const pings: number[] = []; const PING_COUNT = 10;
    for (let i = 0; i < PING_COUNT; i++) {
      const start = performance.now();
      try { await fetch(appendCacheBuster(resolvedServerUrl), { method: 'HEAD', cache: 'no-store', signal: AbortSignal.timeout(3000) }); pings.push(performance.now() - start); }
      catch (e) { pings.push(3001); }
      const currentPing = pings[pings.length - 1];
      const jitter = pings.length > 1 ? Math.abs(currentPing - pings[pings.length - 2]) : 0;
      callbacks.onPingUpdate(Math.round(Math.min(3000, currentPing)), Math.round(jitter));
      callbacks.onProgress(((i + 1) / PING_COUNT) * 10);
      await sleep(350);
    }
    const successfulPings = pings.filter(p => p <= 3000);
    if (successfulPings.length < PING_COUNT / 2) throw new Error("Could not establish a stable connection.");
    result.ping = Math.round(successfulPings.reduce((a, b) => a + b, 0) / successfulPings.length);
    const jitters = successfulPings.slice(1).map((p, i) => Math.abs(p - successfulPings[i]));
    result.jitter = jitters.length > 0 ? Math.round(jitters.reduce((a, b) => a + b, 0) / jitters.length) : 0;
    callbacks.onPingUpdate(result.ping, result.jitter); await sleep(500);

    // 2. Download Test
    callbacks.onStateChange('testing-download');
    const smoothDownload = createSpeedSmoother();
    const DOWNLOAD_CONNECTIONS = connectionMode === 'multi' ? 8 : 1;
    let totalLoadedBytes = 0;
    let firstError: Error | null = null;
    let isDownloadActive = true;
    const downloadStartTime = performance.now();
    const downloadController = new AbortController();

    const progressUpdater = setInterval(() => {
        if (!isDownloadActive) return;
        const elapsed = performance.now() - downloadStartTime;
        const downloadProgress = Math.min(1, elapsed / (TEST_DURATION_MS + WARMUP_MS));
        callbacks.onProgress(10 + downloadProgress * 45);

        if (elapsed > WARMUP_MS) {
            const speedMbps = (totalLoadedBytes * 8) / ((elapsed - WARMUP_MS) * 1000);
            callbacks.onDownloadUpdate(parseFloat(smoothDownload(speedMbps).toFixed(2)));
        }
    }, 250);
    
    const downloadWorker = async () => {
        try {
            const response = await fetch(appendCacheBuster(resolvedServerUrl), { signal: downloadController.signal, cache: 'no-store' });
            if (!response.ok || !response.body) throw new Error(`Server error (${response.status})`);
            
            const reader = response.body.getReader();
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                if (value) totalLoadedBytes += value.length;
            }
        } catch (e) {
            // This will catch AbortError when the test ends, or other fetch errors
            if (e instanceof Error && e.name !== 'AbortError' && !firstError) {
                firstError = e;
            }
        }
    };
    
    const downloadPromises = Array.from({ length: DOWNLOAD_CONNECTIONS }, () => downloadWorker());

    await sleep(TEST_DURATION_MS + WARMUP_MS);

    isDownloadActive = false;
    downloadController.abort();
    clearInterval(progressUpdater);
    await Promise.all(downloadPromises);

    const finalElapsedMs = performance.now() - downloadStartTime;
    const finalTestDurationSeconds = (finalElapsedMs > WARMUP_MS) ? (finalElapsedMs - WARMUP_MS) / 1000 : 0;
    
    if (finalTestDurationSeconds > 0 && totalLoadedBytes > 0) {
        result.download = parseFloat(((totalLoadedBytes * 8) / (finalTestDurationSeconds * 1000000)).toFixed(2));
    } else {
        result.download = 0;
        if (firstError && firstError.message.includes('Server error')) {
            result.downloadError = firstError.message;
        } else if (firstError && firstError.name === 'TypeError') {
            result.downloadError = "Download failed due to CORS or network issue.";
        } else if (totalLoadedBytes === 0) {
             result.downloadError = "Test failed to transfer any data.";
        }
    }
    callbacks.onDownloadUpdate(result.download); await sleep(500);

    // 3. Upload Test
    callbacks.onStateChange('testing-upload');
    const smoothUpload = createSpeedSmoother(); let totalUploadedBytes = 0, firstUploadError: Error | null = null;
    const UPLOAD_CONNECTIONS = connectionMode === 'multi' ? 8 : 1;
    const UPLOAD_CHUNK_SIZE_BYTES = connectionMode === 'multi' ? 1024 * 1024 : 1024 * 256;
    const dataChunk = new Blob([new Uint8Array(UPLOAD_CHUNK_SIZE_BYTES)], { type: 'application/octet-stream' });
    let isUploadActive = true; const uploadStartTime = performance.now(); const uploadController = new AbortController();
    
    const uploadProgressUpdater = setInterval(() => {
      if (!isUploadActive) return; const elapsed = performance.now() - uploadStartTime;
      const uploadProgress = Math.min(1, elapsed / (TEST_DURATION_MS + WARMUP_MS));
      callbacks.onProgress(55 + uploadProgress * 45);

      if (elapsed > WARMUP_MS) { const speedMbps = (totalUploadedBytes * 8) / ((elapsed - WARMUP_MS) * 1000); callbacks.onUploadUpdate(parseFloat(smoothUpload(speedMbps).toFixed(2))); }
    }, 250);
    
    const uploadWorker = async () => {
      let inFlight = false;
      while (isUploadActive) {
        if (!inFlight) {
          inFlight = true;
          fetch(appendCacheBuster(resolvedServerUrl), { method: 'POST', body: dataChunk, signal: uploadController.signal, cache: 'no-store' })
            .then(response => {
              if (isUploadActive) {
                if (response.ok) {
                  totalUploadedBytes += UPLOAD_CHUNK_SIZE_BYTES;
                } else {
                  if (!firstUploadError) firstUploadError = new Error(`Server error (${response.status})`);
                  if (UPLOAD_CONNECTIONS === 1) isUploadActive = false;
                }
              }
            })
            .catch(e => {
              if (isUploadActive && e instanceof Error && e.name !== 'AbortError') {
                if (!firstUploadError) firstUploadError = e;
              }
            })
            .finally(() => {
              inFlight = false;
            });
        }
        await sleep(10);
      }
    };

    const uploadPromises = Array.from({ length: UPLOAD_CONNECTIONS }, uploadWorker);
    await sleep(TEST_DURATION_MS + WARMUP_MS);
    isUploadActive = false; uploadController.abort(); clearInterval(uploadProgressUpdater);
    await Promise.all(uploadPromises).catch(() => {});
    
    const finalUploadTimeMs = performance.now() - uploadStartTime;
    const finalUploadTestDurationSeconds = (finalUploadTimeMs > WARMUP_MS) ? (finalUploadTimeMs - WARMUP_MS) / 1000 : 0;
    result.upload = (finalUploadTestDurationSeconds > 0 && totalUploadedBytes > 0) ? parseFloat(((totalUploadedBytes * 8) / (finalUploadTestDurationSeconds * 1000000)).toFixed(2)) : 0;
    if (totalUploadedBytes === 0 && firstUploadError) { result.uploadError = firstUploadError instanceof TypeError ? "Upload failed. Server may not support POST/CORS." : firstUploadError.message; }
    else if (totalUploadedBytes === 0 && finalUploadTimeMs > WARMUP_MS) { result.uploadError = "Upload test transferred no data."; }
    callbacks.onUploadUpdate(result.upload); await sleep(500);

    callbacks.onComplete(result);
  } catch(error) { callbacks.onError(error instanceof Error ? error.message : "An unknown error occurred."); }
};

export const pingServer = async (server: Server): Promise<number> => {
    try {
        const url = resolveServerUrl(server.url);
        const start = performance.now();
        await fetch(appendCacheBuster(url), { method: 'HEAD', cache: 'no-store', signal: AbortSignal.timeout(2500) });
        return Math.round(performance.now() - start);
    } catch (e) {
        return 9999;
    }
};

export const validateAndCreateCustomServer = async (url: string): Promise<Server> => {
    const ping = await pingServer({ url } as Server);
    if (ping >= 9999) throw new Error("Server is unreachable or has a restrictive CORS policy.");
    const absoluteUrl = resolveServerUrl(url);
    const hostname = new URL(absoluteUrl).hostname;
    return { id: `custom_${Date.now()}`, name: hostname, location: `${hostname}, Custom`, city: hostname, country: 'Custom', url, ping };
};

export const getUserLocation = (): Promise<{ latitude: number; longitude: number } | null> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      }),
      (error) => {
        console.warn(`Geolocation error: ${error.message}`);
        resolve(null);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 1000 * 60 * 5 }
    );
  });
};

export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Radius of the Earth in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
};